import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ApiService } from './api.service';
import { environment } from '@env/environment';
import {
  GetGradeUnitListDto,
  GradeUnitListItemDTO,
  IGradeUnitDTO,
  IGradeUnitListItemDTO,
} from '@models/grade-unit/grade-unit';
import { IGradeUnitRequest } from '@models/grade-unit/grade-unit';
import { FileService } from '../file.service';
import { GradeLevel, gradeLevelMap, GradeRoute } from '@models/grade-level';
import { IPagedResults } from '@models/response';
import { NotificationService } from '../notification.service';
import { ISelectOptions } from '@models/form/select';

@Injectable({
  providedIn: 'root',
})
export class GradeUnitService {
  private url: string = `${environment.apiRoute}`;

  constructor(
    private apiService: ApiService,
    private fileService: FileService,
    private notificationService: NotificationService,
  ) {
    this.url = `${environment.apiRoute}`;
  }

  public getAllUnits(): Observable<GetGradeUnitListDto> {
    const endpoint = `${this.url}/units`;
    return this.apiService.get<GetGradeUnitListDto>(endpoint);
  }

  public getGradeUnitByUnitNumber(
    gradeLevel: string,
    unitNumber: string,
  ): Observable<IGradeUnitDTO> {
    const endpoint = `${this.url}/grades/${gradeLevel}/units/${unitNumber}`;
    if (gradeLevel === '0') gradeLevel = 'k';
    return this.apiService.get<IGradeUnitDTO>(endpoint);
  }

  public getGradeUnitListItemByUnitNumber(
    gradeLevel: string,
    unitNumber: string,
  ): Observable<IGradeUnitListItemDTO> {
    const endpoint = `${this.url}/grades/${gradeLevel}/units/${unitNumber}`;
    if (gradeLevel === '0') gradeLevel = 'k';
    return this.apiService.get<IGradeUnitDTO>(endpoint).pipe(
      map((unit) => {
        const guliDTO: IGradeUnitListItemDTO = new GradeUnitListItemDTO();
        guliDTO.unitId = unit.id;
        guliDTO.status = unit.unitStatus;
        guliDTO.unitCoverImageId = unit.unitCoverImage.name;
        guliDTO.unitCoverImageUrl = unit.unitCoverImage.url;
        guliDTO.unitDescription = unit.unitDescription;
        guliDTO.unitTitle = unit.unitTitle;
        guliDTO.unitGradeLevel = unit.unitGradeLevel;
        guliDTO.unitNumber = unit.unitNumber;
        return guliDTO;
      }),
    );
  }

  public getGradeUnitListItemByUnitId(
    unitId: string,
  ): Observable<IGradeUnitListItemDTO> {
    const endpoint = `${this.url}/units/${unitId}`;

    return this.apiService.get<IGradeUnitDTO>(endpoint).pipe(
      map((unit) => {
        const guliDTO: IGradeUnitListItemDTO = new GradeUnitListItemDTO();
        guliDTO.unitId = unit.id;
        guliDTO.status = unit.unitStatus;
        guliDTO.unitCoverImageId = unit.unitCoverImage.fileName;
        guliDTO.unitCoverImageUrl = unit.unitCoverImage.signedUrl;
        guliDTO.unitDescription = unit.unitDescription;
        guliDTO.unitTitle = unit.unitTitle;
        guliDTO.unitGradeLevel = unit.unitGradeLevel;
        guliDTO.unitNumber = unit.unitNumber;
        return guliDTO;
      }),
    );
  }

  public getGradeUnitListByGradeLevel(
    gradeLevel: string,
  ): Observable<IPagedResults<IGradeUnitListItemDTO>> {
    const endpoint = `${this.url}/grades/${gradeLevel}/units`;
    if (gradeLevel === '0') gradeLevel = 'k';
    return this.apiService.get<IPagedResults<IGradeUnitListItemDTO>>(endpoint);
  }

  public getStudentGradeUnitListByGradeLevel(
    studentId: string,
    gradeLevel: GradeRoute,
  ): Observable<IPagedResults<IGradeUnitListItemDTO>> {
    const endpoint = `${this.url}/students/${studentId}/grades/${gradeLevel}/units`;

    return this.apiService.get<IPagedResults<IGradeUnitListItemDTO>>(endpoint);
  }

  public createGradeUnit(
    request: IGradeUnitRequest,
  ): Observable<IGradeUnitDTO> {
    const payload = this.setFormDataProps(request);
    const gradeLevelMapper = gradeLevelMap.find(
      (gradeLevel) => gradeLevel.gradeEnum === request.unitGradeLevel,
    );
    const grade = gradeLevelMapper.gradeRoute;
    const endpoint = `${this.url}/grades/${grade}/units`;
    const headers = new HttpHeaders();
    headers.set('Content-Type', 'multipart/form-data');

    return this.apiService
      .post<IGradeUnitDTO, FormData>(endpoint, payload, { headers })
      .pipe(
        tap(() => {
          const message = `Unit created.`;
          return this.notificationService.showSuccess([message]);
        }),
      );
  }

  public updateGradeUnit(
    request: IGradeUnitRequest,
    unitNumber: number,
  ): Observable<IGradeUnitDTO> {
    const payload = this.setFormDataProps(request);
    const gradeLevelMapper = gradeLevelMap.find(
      (gradeLevel) => gradeLevel.gradeEnum === request.unitGradeLevel,
    );
    const grade = gradeLevelMapper.gradeRoute;
    const endpoint = `${this.url}/grades/${grade}/units/${unitNumber}`;
    const headers = new HttpHeaders();
    headers.set('Content-Type', 'multipart/form-data');

    return this.apiService
      .patch<IGradeUnitDTO, FormData>(endpoint, payload, { headers })
      .pipe(
        tap(() => {
          const message = `Unit updated.`;
          return this.notificationService.showSuccess([message]);
        }),
      );
  }

  public deleteGradeUnit(
    gradeNumber: string,
    unitNumber: number,
  ): Observable<IGradeUnitDTO> {
    const endpoint = `${this.url}/grades/${gradeNumber}/units/${unitNumber}`;

    return this.apiService.delete<IGradeUnitDTO>(endpoint).pipe(
      tap((unit: IGradeUnitDTO) => {
        let message = '';
        if (unit.deletedAt) {
          message = `Unit hidden.`;
        } else {
          message = `Unit is viewable.`;
        }
        return this.notificationService.showSuccess([message]);
      }),
    );
  }

  /**
   * Convert the request values to `FormData` to be able to add the `FileList` to upload.
   */
  private setFormDataProps(request: IGradeUnitRequest): FormData {
    const props: ISelectOptions[] = [
      {
        name: 'unitTitle',
        value: request.unitTitle,
      },
      {
        name: 'unitDescription',
        value: request.unitDescription,
      },
      {
        name: 'unitNumber',
        value: request.unitNumber.toString(),
      },
      {
        name: 'unitGradeLevel',
        value: request.unitGradeLevel,
      },
      {
        name: 'unitNGSSCategory',
        value: request.unitNGSSCategory,
      },
      {
        name: 'isDemo',
        value: request.isDemo.toString(),
      },
      {
        name: 'unitNGSSMetricNumber',
        value: request.unitNGSSMetricNumber,
      },
      {
        name: 'unitNGSSMetricDescription',
        value: request.unitNGSSMetricDescription,
      },
      {
        name: 'unitStatus',
        value: request.unitStatus,
      },
    ];
    return this.fileService.buildFormData(
      request.unitCoverImage,
      props,
      'unitCoverImage',
    );
  }
}
